import { clearFrame, composite, createFrame, type PixelFrame, type Pose } from "pixelfx";
import type { FxSystem } from "../fx/fxSystem";
import { ARENA_ART_W, ART_FLOOR, ART_H, ART_W, SCREEN_H, SCREEN_W, toArtX, toArtY } from "../shared/screen";
import {
  FIGHTER,
  FULL_METER,
  isSlowed,
  VICTIM_START,
  type FighterState,
  type MatchRules,
  type MatchState,
} from "../sim";
import type { OverlayWeights } from "./poseOverlay";
import { drawRageAura } from "./rageAura";
import type { DustJump } from "./dust3d";
import { SecondaryMotion } from "./secondaryMotion";
import { poseKind } from "./blendTable";
import { Stage3D, type FighterPose } from "./stage3d";
import { cloudCenterX, drawShedding, drawTeleportCloud, type CloudFlight } from "./teleportCloud";
import { ArenaDecor, foundryBackground } from "./arena";
import { Camera } from "./camera";
import { maskContrast } from "./contrast";
import { Hud, outlinedText } from "./hud";
import { drawBody } from "./mannequin/body";
import type { RingView } from "./decisiveFx3d";
import { koBody } from "./koBody";
import { reachLean } from "./reachLean";
import { reactionShot, shotFor, stepShot, superShot, superShot3d } from "./sprites/animation";
import { cinematicZoom, drawVignette, superFlinch } from "./superView";
import { loadAtlas, type HeroAtlas } from "./sprites/atlas";
import { drawSprite } from "./sprites/draw";
import { poseFor, superPose } from "./mannequin/pose";
import { drawInputIndicator, type InputView } from "./inputIndicator";
import { drawControlsPage } from "./controlsPage";
import { drawPauseMenu, type PauseView } from "./pauseMenu";

/** Затемнённая копия фона (ABGR): каналы × k. */
function darken(source: Uint32Array, k: number): Uint32Array {
  const out = new Uint32Array(source.length);
  for (let index = 0; index < source.length; index++) {
    const color = source[index] ?? 0;
    const r = Math.round((color & 255) * k);
    const g = Math.round(((color >>> 8) & 255) * k);
    const b = Math.round(((color >>> 16) & 255) * k);
    out[index] = (0xff000000 | (b << 16) | (g << 8) | r) >>> 0;
  }
  return out;
}

/** Бросок: и бросающий, и брошенный — в пространстве пары клипов от точки начала броска. */
function pairOf(state: MatchState, rules: MatchRules, side: 0 | 1): { paired?: { originX: number; facing: number } } {
  const fighter = state.fighters[side];
  const thrower =
    fighter.state === FIGHTER.thrown
      ? state.fighters[side === 0 ? 1 : 0]
      : fighter.state === FIGHTER.ability && rules.kits[side][fighter.ability]?.input.grab
        ? fighter
        : null;
  if (!thrower) return {};
  // каждая модель пары — в начале своего пути: бросающий — где стоял, соперник — впереди него (sim/throw.ts)
  const start = thrower === fighter ? 0 : VICTIM_START * thrower.facing;
  return { paired: { originX: thrower.throwX / 256 + start, facing: thrower.facing } };
}

/** Невидимка гаснет за столько последних кадров — проявляется плавно. */
const CLOAK_FADE = 10;

export interface Overlay {
  readonly names: readonly [string, string];
  readonly banner: readonly string[];
  /** Яркость свечения эффектов, 0–1 (настройка «яркость вспышек»). */
  readonly flash: number;
  /** Сила тряски, 0–1. */
  readonly shake: number;
  readonly pause?: PauseView;
  /** Экран «Управление» поверх паузы. */
  readonly controls?: boolean;
  /** Табло связи онлайн: пинг, откат, ожидания. */
  readonly status?: string;
  /** Решающий удар: сила особого ракурса, угол облёта, чей удар, вспышка (app/decisiveView.ts). */
  readonly decisive?: {
    readonly orbit: number;
    readonly angle: number;
    readonly by: number;
    readonly flash: number;
    readonly ring: RingView | null;
  };
  /** Чей это экран онлайн (0, 1); нет — один экран на двоих: невидимку оба видят одинаково (слабым контуром). */
  readonly viewer?: 0 | 1;
  /** Подпись листа приёмов: «кнопка → что должно быть». */
  readonly caption?: string;
  /** Индикатор нажатий: что игра видит у каждого игрока. */
  readonly inputs?: {
    readonly players: readonly InputView[];
    readonly lastKey: string;
    readonly pads: readonly string[];
  };
}

/** «НОКАУТ» — когда ракурс решающего удара почти сошёл: камера отъехала. */
const STAMP_AFTER_ORBIT = 0.15;

/**
 * Кадр игры: сетка арта 960×540 (арена, эффекты pixelfx, бойцы — всё на одной сетке) → ×2 → Full HD 1920×1080 →
 * HUD и меню в полном разрешении. Камера и тряска — целые пиксели арта. Снимок мира только читается.
 */
export class Renderer {
  private readonly frame: PixelFrame = createFrame(ART_W, ART_H);
  private readonly image = new ImageData(ART_W, ART_H);
  private readonly art = document.createElement("canvas");
  private readonly artContext: CanvasRenderingContext2D;
  private readonly context: CanvasRenderingContext2D;
  private readonly background = foundryBackground();
  /** Фон заставки суперприёма: та же арена, втрое темнее — бойцы и частицы выходят вперёд. */
  private readonly darkBackground = darken(this.background, 0.32);
  private readonly window = new Uint32Array(ART_W * ART_H);
  private readonly mask = new Uint8Array(ART_W * ART_H);
  private readonly decor = new ArenaDecor();
  private readonly hud = new Hud();
  readonly camera = new Camera();
  /** Откуда начался скачок каждого бойца, пиксели мира (камера стоит на ней, облако летит из неё). */
  private readonly blinkFrom: [number | null, number | null] = [null, null];
  /** Холст 960×540 для замера контраста в 3D (только съёмка). */
  private readonly measureCanvas = Object.assign(document.createElement("canvas"), { width: ART_W, height: ART_H });
  /** Атласы захвата движений по сторонам; пока не загрузились — рисуется запасной манекен. */
  private atlases: [HeroAtlas | null, HeroAtlas | null] = [null, null];
  readonly ready: Promise<void>;
  /** Живое 3D (ADR 0003): арена и бойцы — three.js; null — WebGL недоступен, рисуем по-старому пикселями. */
  private readonly stage: Stage3D | null;
  private readonly secondary = new SecondaryMotion();
  /** Контраст каждого бойца к окружению в последнем кадре — только для замеров съёмки (measure = true). */
  readonly contrast: [number, number] = [0, 0];
  measure = false;
  private lastFrame = 0;
  /** Раунд прошлого кадра: сменился — склейка, модели не встают и не плывут на места (консультант 05.11.2026). */
  private lastRound = -1;

  constructor(readonly canvas: HTMLCanvasElement) {
    canvas.width = SCREEN_W;
    canvas.height = SCREEN_H;
    this.art.width = ART_W;
    this.art.height = ART_H;
    const artContext = this.art.getContext("2d");
    const context = canvas.getContext("2d");
    if (!artContext || !context) throw new Error("Canvas 2D недоступен");
    this.artContext = artContext;
    this.context = context;
    this.stage = (() => {
      try {
        return new Stage3D();
      } catch {
        return null;
      }
    })();
    const atlases = Promise.all([loadAtlas("mannequin", "p1"), loadAtlas("mannequin", "p2")]).then(
      ([first, second]) => {
        this.atlases = [first, second];
      },
    );
    this.ready = Promise.all([atlases, this.stage?.ready]).then(() => undefined);
  }

  /** Чистый фон арены (вся арена в сетке арта) — для постановочных роликов движений. */
  backgroundCanvas(): HTMLCanvasElement {
    const canvas = document.createElement("canvas");
    canvas.width = ARENA_ART_W;
    canvas.height = ART_H;
    const context = canvas.getContext("2d");
    if (!context) throw new Error("Canvas 2D недоступен");
    const image = new ImageData(ARENA_ART_W, ART_H);
    image.data.set(
      new Uint8ClampedArray(this.background.buffer, this.background.byteOffset, this.background.byteLength),
    );
    context.putImageData(image, 0, 0);
    return canvas;
  }

  /** Холст 960×540 без HUD — для замера вспышек при съёмке; в 3D — настоящий кадр (сцена и эффекты), а не слой эффектов. */
  get artCanvas(): HTMLCanvasElement {
    return this.stage ? this.measureCanvas : this.art;
  }

  draw(state: MatchState, rules: MatchRules, fx: FxSystem, overlay: Overlay): void {
    fx.ambient = !this.stage;
    const stage = this.stage;
    fx.anchor = stage ? (side, x, y): readonly [number, number] | null => stage.anchor(side, x, y) : null;
    fx.touch = stage !== null;
    const elapsed = Math.max(0, Math.min(4, state.frame - this.lastFrame));
    this.lastFrame = state.frame;
    const flights = [this.flightOf(state, rules, 0), this.flightOf(state, rules, 1)] as const;
    // камера на скачке стоит (Джон 21:19: «камеру не трогать, пускай переместится, потом плавно догонит»)
    this.camera.focus = [this.blinkFrom[0], this.blinkFrom[1]];
    for (let tick = 0; tick < elapsed; tick++) this.camera.update(state);
    this.decor.update(elapsed / 60);

    const shake =
      fx.shakeFrames > 0 ? Math.round((state.frame % 2 === 0 ? 1 : -1) * fx.shakePower * 2 * overlay.shake) : 0;
    const left = this.camera.x - shake;
    const frame = this.frame;
    frame.ox = left;
    frame.oy = 0;
    const cinematic = state.cinematic >= 0;
    const source = cinematic ? this.darkBackground : this.background;
    if (this.stage)
      this.window.fill(0); // 3D: слой эффектов прозрачный поверх сцены
    else
      for (let y = 0; y < ART_H; y++) {
        const from = y * ARENA_ART_W + Math.max(0, Math.min(ARENA_ART_W - ART_W, left));
        this.window.set(source.subarray(from, from + ART_W), y * ART_W);
      }
    clearFrame(frame);
    this.mask.fill(0);
    if (!this.stage) this.decor.draw(frame); // в 3D факелы и пар — в сцене
    fx.drawBehind(frame);
    const order = state.fighters[0].state === FIGHTER.ability ? [1, 0] : [0, 1];
    const poses: FighterPose[] = [];
    for (const side of order) {
      const fighter = state.fighters[side as 0 | 1];
      const x = toArtX(fighter.x / 256) - left;
      const y = toArtY(fighter.y / 256);
      const atlas = this.atlases[side];
      const move = cinematic ? rules.supers[state.cinematic as 0 | 1] : null;
      const shot = move
        ? state.cinematic === side
          ? superShot(state.cinematicFrame)
          : shotFor(fighter, undefined, state.frame, false)
        : shotFor(fighter, rules.kits[side as 0 | 1][fighter.ability], state.frame, fighter.running > 0);
      const flight = flights[side as 0 | 1];
      if (this.stage) {
        // 3D: дробный кадр (поза каждый кадр мира); шаг — клипом шага по пройденному пути, стопы не едут
        const walking =
          (fighter.state === FIGHTER.walkForward && fighter.running === 0) || fighter.state === FIGHTER.walkBack;
        const smooth = move
          ? state.cinematic === side
            ? superShot3d(move, state.cinematicFrame, "attacker")
            : fighter.state === FIGHTER.superVictim
              ? superShot3d(move, state.cinematicFrame, "victim")
              : shotFor(fighter, undefined, state.frame, false, true)
          : walking
            ? stepShot(fighter)
            : fighter.state === FIGHTER.hitstun
              ? reactionShot(fighter)
              : shotFor(fighter, rules.kits[side as 0 | 1][fighter.ability], state.frame, fighter.running > 0, true);
        poses[side] = {
          shot: smooth,
          tick: state.frame,
          since: fighter.stateFrame,
          ...koBody(state, fighter),
          lean: reachLean(
            fighter,
            state.fighters[side === 0 ? 1 : 0],
            rules.kits[side as 0 | 1][fighter.ability],
            rules.heroes[side === 0 ? 1 : 0].reactionBody,
          ),
          // держит удар: в блоке — полностью, в лёгкой реакции — наполовину; полосы — пока скользит
          brace: fighter.state === FIGHTER.blockstun ? 1 : fighter.state === FIGHTER.hitstun ? 0.5 : 0,
          slide: Math.abs(fighter.vx / 256),
          kind: poseKind(fighter, rules, side as 0 | 1),
          ...pairOf(state, rules, side as 0 | 1),
          cloak: fighter.invisible > 0 ? Math.min(1, fighter.invisible / CLOAK_FADE) : 0,
          cloakEdge: overlay.viewer === undefined ? 0.5 : overlay.viewer === side ? 0.9 : 0.15,
          flinch:
            fighter.state === FIGHTER.hitstun
              ? Renderer.flinchOf(fighter)
              : move && fighter.state === FIGHTER.superVictim
                ? superFlinch(move, state.cinematicFrame)
                : 0,
          ...this.secondary.weights(state, side as 0 | 1),
          x: fighter.x / 256,
          y: fighter.y / 256,
          facing: smooth.mirror ? -fighter.facing : fighter.facing,
          hidden: flight !== null,
        };
      }
      const warning = this.blinkWarning(state, rules, side as 0 | 1);
      if (warning !== null && atlas && !this.stage) drawShedding(frame, atlas, x, y, fighter.facing, side, warning);
      if (this.stage) continue; // боец и скачок-рой — в 3D-сцене
      if (flight && atlas) {
        drawTeleportCloud(frame, atlas, {
          ...flight.cloud,
          fromX: flight.cloud.fromX - left,
          toX: flight.cloud.toX - left,
        });
        continue;
      }
      const drawn = atlas
        ? drawSprite(
            frame,
            this.mask,
            atlas,
            shot,
            x,
            y,
            shot.mirror ? -fighter.facing : fighter.facing,
            side,
            fighter.invisible > 0,
            state.frame,
          )
        : false;
      if (!drawn) {
        drawBody({
          frame,
          mask: this.mask,
          pose: this.poseOf(state, rules, side as 0 | 1),
          facing: fighter.facing,
          lying: (fighter.state === FIGHTER.knockdown || fighter.state === FIGHTER.ko) && fighter.y >= 0,
          invisible: fighter.invisible > 0,
          x,
          y,
          side,
          tick: state.frame,
        });
      }
    }
    // аура Ярости: полная шкала — свечение по контуру, огонь вверх, искры вокруг (только свет, не краска)
    this.aura(state, left, cinematic);
    fx.drawFront(frame);
    composite(frame, this.image.data, this.stage ? null : this.window, overlay.flash);
    this.artContext.putImageData(this.image, 0, 0);
    if (this.measure && !this.stage)
      for (const side of [0, 1]) this.contrast[side] = maskContrast(this.image, this.mask, side);

    const context = this.context;
    context.imageSmoothingEnabled = false;
    if (this.stage) {
      // 3D: сцена во весь кадр, поверх — прозрачный слой эффектов ×2, наезда заставки пока нет
      const zoom = cinematic ? cinematicZoom(rules.supers[state.cinematic as 0 | 1], state.cinematicFrame) : 1;
      const focusX = (state.fighters[0].x + state.fighters[1].x) / 2 / 256;
      // на решающем ударе камера держит в кадре и отлетевшего: смотрит выше, когда он высоко
      const lift = 0;
      const decisive = overlay.decisive;
      const by = decisive ? state.fighters[decisive.by === 1 ? 1 : 0] : null;
      const orbit =
        decisive && by
          ? {
              strength: decisive.orbit,
              angle: decisive.angle,
              facing: by.facing,
              attackerX: by.x / 256,
              ring: decisive.ring,
            }
          : undefined;
      const shots = state.shots.map((shot) => ({
        live: shot.live === 1,
        x: shot.x / 256,
        y: -shot.y / 256,
        vx: shot.vx,
        age: shot.age,
      }));
      const jumps = [this.dustJump(state, rules, 0), this.dustJump(state, rules, 1)];
      const cut = state.round !== this.lastRound;
      this.lastRound = state.round;
      this.stage.render(
        poses,
        left,
        jumps,
        state.frame,
        { zoom, focusX, lift, cut, ...(orbit ? { orbit } : {}) },
        shots,
      );
      // удары и блоки, чей кадр картинка уже нарисовала, — искры в точку касания на этой позе
      const drawn = this.stage;
      if (fx.place(state, (side) => drawn.strikePoint(side)) > 0) {
        // в 3D слой эффектов — только эффекты и аура: перерисовать, чтобы искра была на этой же позе, а не кадром позже
        clearFrame(frame);
        fx.drawBehind(frame);
        this.aura(state, left, cinematic);
        fx.drawFront(frame);
        composite(frame, this.image.data, null, overlay.flash);
        this.artContext.putImageData(this.image, 0, 0);
      }
      context.drawImage(this.stage.canvas, 0, 0);
      if (zoom > 1) drawVignette(context, Math.min(1, (zoom - 1) / 0.7));
      if (decisive) {
        drawVignette(context, decisive.orbit);
        // попадание в решающем ударе — белая вспышка на кадр-другой
        if (decisive.flash > 0) {
          context.fillStyle = `rgba(255, 255, 255, ${(0.75 * decisive.flash).toFixed(3)})`;
          context.fillRect(0, 0, SCREEN_W, SCREEN_H);
        }
      }
      context.drawImage(this.art, 0, 0, ART_W, ART_H, 0, 0, SCREEN_W, SCREEN_H);
      if (this.measure) this.measure3d();
    } else {
      const view = this.cinematicView(state, rules, left);
      context.drawImage(this.art, view.x, view.y, view.w, view.h, 0, 0, SCREEN_W, SCREEN_H);
    }
    const holdStamp = isSlowed(state) || (overlay.decisive?.orbit ?? 0) > STAMP_AFTER_ORBIT;
    this.hud.draw(context, state, rules, overlay.names, overlay.banner, holdStamp);
    const move = cinematic ? rules.supers[state.cinematic as 0 | 1] : null;
    if (move && state.cinematicFrame >= 4 && state.cinematicFrame < 80)
      outlinedText(context, move.name, SCREEN_W / 2, 150, 150, "#ffd24a");
    if (overlay.inputs)
      drawInputIndicator(context, overlay.inputs.players, overlay.inputs.lastKey, overlay.inputs.pads);
    if (overlay.status) outlinedText(context, overlay.status, SCREEN_W / 2, 1050, 22, "#a8a0b8", "center", "normal");
    if (overlay.caption) outlinedText(context, overlay.caption, SCREEN_W / 2, 215, 36, "#ffe9a8", "center", "normal");
    if (overlay.controls) drawControlsPage(context, rules.kits[0], rules.supers[0]);
    else if (overlay.pause) drawPauseMenu(context, overlay.pause);
  }

  /**
   * Скачок-пыльца: боец в окне vanish своей способности — облако частиц. Начало — где он стоит, конец — куда перенесёт
   * телепорт (та же формула, что в мире), пиксели арты по всей арене; после переноса — уже на месте.
   */
  private flightOf(state: MatchState, rules: MatchRules, side: 0 | 1): { cloud: CloudFlight; worldX: number } | null {
    const fighter = state.fighters[side];
    const ability = fighter.state === FIGHTER.ability ? rules.kits[side][fighter.ability] : undefined;
    const vanish = ability?.vanish;
    const teleport = ability?.actions?.find((cue) => cue.type === "teleport");
    // откуда скачок: запоминаем до переноса; камера стоит на этой точке до конца облака
    if (!ability || !vanish) {
      this.blinkFrom[side] = null;
      return null;
    }
    if (teleport && fighter.abilityFrame <= teleport.frame) this.blinkFrom[side] = fighter.x / 256;
    if (fighter.abilityFrame >= vanish[1]) this.blinkFrom[side] = null;
    if (fighter.abilityFrame < vanish[0] || fighter.abilityFrame >= vanish[1]) return null;
    const other = state.fighters[side === 0 ? 1 : 0];
    const moved = teleport !== undefined && fighter.abilityFrame > teleport.frame;
    const half = rules.heroes[side].width / 2;
    const behind = other.x - fighter.x >= 0 ? 1 : -1;
    const target = moved
      ? fighter.x / 256
      : Math.min(
          Math.max(other.x / 256 + behind * (teleport?.params?.distance ?? 80), rules.arena.left + half),
          rules.arena.right - half,
        );
    const start = moved ? (this.blinkFrom[side] ?? target) : fighter.x / 256;
    const progress = (fighter.abilityFrame - vanish[0]) / (vanish[1] - vanish[0]);
    const cloud: CloudFlight = {
      progress,
      fromX: toArtX(start),
      toX: toArtX(target),
      feet: toArtY(fighter.y / 256),
      facing: moved ? -fighter.facing : fighter.facing,
      side,
    };
    return { cloud, worldX: start + (target - start) * cloudCenterX({ ...cloud, fromX: 0, toX: 1 }) };
  }

  /** Замер контраста в 3D: кадр (сцена + эффекты, без HUD) в 960×540 и маски бойцов отдельным проходом. */
  private measure3d(): void {
    if (!this.stage) return;
    const scratch = this.measureCanvas;
    const context = scratch.getContext("2d", { willReadFrequently: true });
    if (!context) return;
    context.drawImage(this.canvas, 0, 0, ART_W, ART_H);
    const image = context.getImageData(0, 0, ART_W, ART_H);
    const mask = this.stage.masks(ART_W, ART_H);
    for (const side of [0, 1]) this.contrast[side] = maskContrast(image, mask, side);
  }

  /** Скачок для роя 3D: от замаха до конца сборки; откуда, куда, куда смотрит, где соперник. */
  private dustJump(state: MatchState, rules: MatchRules, side: 0 | 1): DustJump | null {
    const fighter = state.fighters[side];
    if (fighter.state !== FIGHTER.ability) return null;
    const ability = rules.kits[side][fighter.ability];
    const vanish = ability?.vanish;
    const teleport = ability?.actions?.find((cue) => cue.type === "teleport");
    if (!ability || !vanish || !teleport || fighter.abilityFrame >= vanish[1]) return null;
    const other = state.fighters[side === 0 ? 1 : 0];
    const moved = fighter.abilityFrame > teleport.frame;
    const half = rules.heroes[side].width / 2;
    const behind = other.x - fighter.x >= 0 ? 1 : -1;
    const toX = moved
      ? fighter.x / 256
      : Math.min(
          Math.max(other.x / 256 + behind * (teleport.params?.distance ?? 80), rules.arena.left + half),
          rules.arena.right - half,
        );
    const fromX = moved ? (this.blinkFrom[side] ?? toX) : fighter.x / 256;
    const fromFacing = moved ? -fighter.facing : fighter.facing;
    return {
      frame: fighter.abilityFrame,
      vanishStart: vanish[0],
      vanishEnd: vanish[1],
      fromX,
      toX,
      fromFacing,
      toFacing: -fromFacing,
      opponentX: other.x / 256,
    };
  }

  /** Замах скачка: доля 0..1 до растворения (видимое предупреждение) или null. */
  private blinkWarning(state: MatchState, rules: MatchRules, side: 0 | 1): number | null {
    const fighter = state.fighters[side];
    if (fighter.state !== FIGHTER.ability) return null;
    const vanish = rules.kits[side][fighter.ability]?.vanish;
    if (!vanish || fighter.abilityFrame >= vanish[0]) return null;
    return (fighter.abilityFrame + 1) / vanish[0];
  }

  /** Экран ожидания онлайна: тёмный фон арены и строки по центру. */
  drawLobby(lines: readonly string[]): void {
    const context = this.context;
    context.fillStyle = "#07060a";
    context.fillRect(0, 0, SCREEN_W, SCREEN_H);
    lines.forEach((line, index) => {
      if (index === 0) outlinedText(context, line, SCREEN_W / 2, 300, 120, "#ffe2b8");
      else outlinedText(context, line, SCREEN_W / 2, 400 + index * 70, 44, "#f0eaf6", "center", "normal");
    });
  }

  private poseOf(state: MatchState, rules: MatchRules, side: 0 | 1): Pose {
    const fighter = state.fighters[side];
    const move = state.cinematic >= 0 ? rules.supers[state.cinematic as 0 | 1] : null;
    if (move) return superPose(move, state.cinematicFrame, state.cinematic === side ? "attacker" : "victim");
    return poseFor(fighter, rules.kits[side][fighter.ability]);
  }

  /**
   * Камера заставки: наезд целым масштабом — ×3 на серии, ×4 на финальном ударе, — на середину между бойцами.
   * Вне заставки — весь кадр арта ×2.
   */
  private cinematicView(
    state: MatchState,
    rules: MatchRules,
    left: number,
  ): { x: number; y: number; w: number; h: number } {
    const move = state.cinematic >= 0 ? rules.supers[state.cinematic as 0 | 1] : null;
    const frame = state.cinematicFrame;
    if (!move || frame < 3) return { x: 0, y: 0, w: ART_W, h: ART_H };
    const zoom = frame >= move.finale.frame - 10 && frame <= move.finale.frame + 12 ? 4 : 3;
    const w = SCREEN_W / zoom;
    const h = SCREEN_H / zoom;
    const middle = (toArtX(state.fighters[0].x / 256) + toArtX(state.fighters[1].x / 256)) / 2 - left;
    const x = Math.round(Math.min(Math.max(middle - w / 2, 0), ART_W - w));
    const y = Math.round(Math.min(Math.max(ART_FLOOR - 100 - h / 2, 0), ART_H - h));
    return { x, y, w, h };
  }

  /** Замер позы 3D для поиска рывков (съёмка); без 3D — пусто. */
  /** Аура Ярости у бойцов с полной шкалой (не на заставке суперприёма). */
  private aura(state: MatchState, left: number, cinematic: boolean): void {
    if (cinematic) return;
    state.fighters.forEach((fighter, side) => {
      if (fighter.meter < FULL_METER || fighter.health <= 0) return;
      drawRageAura(this.frame, this.mask, side, toArtX(fighter.x / 256) - left, toArtY(fighter.y / 256), state.frame);
    });
  }

  /** Обмер клипа: где кисти и стопы на кадре клипа (tools/capture/reach.mjs). */
  measureLimbs(
    clip: string,
    frame: number,
    bones?: readonly string[],
    overlay?: OverlayWeights,
  ): ReturnType<Stage3D["measureLimbs"]> {
    return this.stage?.measureLimbs(clip, frame, bones, overlay) ?? null;
  }

  /** Добавка откидывания в отдаче — та же, что в позе бойца (обмер реакций). */
  static flinchOf(fighter: FighterState): number {
    return fighter.state === FIGHTER.hitstun ? (0.7 + 0.3 * fighter.hurtPower) * Math.exp(-fighter.stateFrame / 6) : 0;
  }

  /** Кисти и стопы в сетке арта (съёмка: замер искр). */
  limbs(): ReturnType<Stage3D["limbsArt"]> {
    return this.stage?.limbsArt() ?? [];
  }

  probe(): ReturnType<Stage3D["poseProbe"]> {
    return this.stage?.poseProbe() ?? [];
  }

  resetHud(state: MatchState): void {
    this.stage?.clearMarks();
    this.hud.reset();
    this.camera.reset(state);
  }
}
